import fp from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Form, useForm, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';

import { Button, TrashCan, cn, AutoSizer } from 'Modules/ui';

import { FactorinGrid } from '../FactorinGrid';
import styles from './index.module.scss';


const RemoveButton = ({ data: { index }, column: { remove } }) => (
    <Button variant='auxiliary' className={styles.remove} onClick={() => remove(index)}>
        <TrashCan />
    </Button>
);

const getRemoveColumn = remove => ({
    Header: () => null,
    Cell: RemoveButton,
    field: 'Remove',
    remove,
    fixedWidth: 6.4,
});

const Table = ({ columns, entityName, fields, remove, ...gridProps }) => {
    const tableColumns = useMemo(() => [
        ...columns,
        ...remove ? [getRemoveColumn(index => remove(fields, index))] : [],
    ], [columns, fields, remove]);
    return (
        <AutoSizer>
            {size => (
                <FactorinGrid
                    columns={tableColumns}
                    getData={index => ({
                        name: `${entityName}[${index}]`,
                        index,
                    })}
                    itemCount={fields.length}
                    {...size}
                    {...gridProps}
                />
            )}
        </AutoSizer>
    );
};

const Buttons = ({ fields, values, className, add, t }) => {
    const { initialize } = useForm();
    const { hasValidationErrors, pristine } = useFormState({
        subscription: {
            hasValidationErrors: true,
            pristine: true,
        },
    });
    const addCallback = useCallback(
        () => add(fields),
        [add, fields],
    );
    const resetChanges = useCallback(
        () => initialize(values),
        [initialize, values],
    );
    return (
        <div className={cn(styles.buttons, className)}>
            {add && (
                <Button variant='accent' onClick={addCallback}>
                    {t('buttons.add')}
                </Button>
            )}
            <Button
                variant='accent'
                disabled={hasValidationErrors || pristine}
                type='submit'
            >
                {t('buttons.save')}
            </Button>
            {!pristine && (
                <Button variant='light' onClick={resetChanges}>
                    {t('buttons.reset')}
                </Button>
            )}
        </div>
    );
};

const CrudTableComponent = ({
    columns, values, classNames = {},
    entityName, arrayProps, gridProps,
    add, remove,
    onSubmit,
    t,
    ...formProps
}) => (
    <Form
        onSubmit={onSubmit}
        mutators={arrayMutators}
        initialValues={values}
        subscription={{
            pristine: true,
            hasValidationErrors: true,
        }}
        {...formProps}
    >
        {({ handleSubmit }) => (
            <form
                onSubmit={handleSubmit}
                className={cn(styles.crudTable, classNames.container)}
            >
                <FieldArray
                    name={entityName}
                    isEqual={fp.isEqual}
                    {...arrayProps}
                >
                    {({ fields }) => (
                        <>
                            <Table
                                columns={columns}
                                entityName={entityName}
                                fields={fields}
                                remove={remove}
                                {...gridProps}
                            />
                            <Buttons
                                className={classNames.buttons}
                                fields={fields}
                                add={add}
                                values={values}
                                t={t}
                            />
                        </>
                    )}
                </FieldArray>
            </form>
        )}
    </Form>
);

export const CrudTable = React.memo(CrudTableComponent);
